import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Button, Clickable } from '@moda/om';
import AlertIcon from '@moda/icons/alert-16';
import CheckmarkIcon from '@moda/icons/checkmark-16';
import { isCompleteZip, normalizeZip } from './deliveryOptions';
import { ZipLookup } from './useZipLookup';

import './DeliveryZipForm.scss';

const LOOKUP_DEBOUNCE_MS = 250;

const HINT_COPY = 'Enter a 5-digit US ZIP code';
const SERVICE_ERROR_COPY = 'We can’t load delivery dates right now. Try again.';
const notFoundCopy = (zip: string) =>
  `We couldn’t find ZIP ${zip}. Check the number and try again.`;

interface Props {
  lookup: ZipLookup;
  serviceError: boolean;
  onPreview: (zip: string | undefined) => void;
  onSubmit: (zip: string) => void;
  onCancel: () => void;
}

export const DeliveryZipForm: React.FC<Props> = ({
  lookup,
  serviceError,
  onPreview,
  onSubmit,
  onCancel
}) => {
  const [value, setValue] = useState('');
  const [incomplete, setIncomplete] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => inputRef.current?.focus(), []);

  useEffect(() => {
    const timeout = window.setTimeout(
      () => onPreview(isCompleteZip(value) ? value : undefined),
      LOOKUP_DEBOUNCE_MS
    );
    return () => window.clearTimeout(timeout);
  }, [value, onPreview]);

  const current = lookup.zip === value ? lookup : undefined;
  const error = (() => {
    if (incomplete) return HINT_COPY;
    if (current?.status === 'not-found') return notFoundCopy(value);
    if (current?.status === 'error' || (current?.status === 'found' && serviceError)) {
      return SERVICE_ERROR_COPY;
    }
    return null;
  })();

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!isCompleteZip(value)) {
      setIncomplete(true);
      inputRef.current?.focus();
      return;
    }
    onPreview(value);
    onSubmit(value);
  };

  return (
    <form
      className={classNames('DeliveryZipForm', { 'DeliveryZipForm--error': error })}
      aria-label="Check delivery date"
      noValidate
      onSubmit={handleSubmit}
    >
      <div className="DeliveryZipForm__row">
        <div className="DeliveryZipForm__field">
          <label className="DeliveryZipForm__label" htmlFor="delivery-zip">
            ZIP code
          </label>
          <input
            ref={inputRef}
            className="DeliveryZipForm__input"
            id="delivery-zip"
            name="delivery-zip"
            type="text"
            inputMode="numeric"
            autoComplete="postal-code"
            maxLength={10}
            pattern="[0-9]{5}(-[0-9]{4})?"
            enterKeyHint="go"
            spellCheck={false}
            aria-describedby="delivery-zip-hint"
            aria-invalid={error ? true : undefined}
            data-1p-ignore=""
            data-lpignore="true"
            data-form-type="other"
            data-bwignore=""
            value={value}
            onChange={event => {
              setIncomplete(false);
              setValue(normalizeZip(event.target.value));
            }}
            onKeyDown={event => {
              if (event.key === 'Escape') onCancel();
            }}
          />
        </div>

        <Button type="submit" className="DeliveryZipForm__update">
          Update
        </Button>

        <Clickable type="button" className="DeliveryZipForm__cancel" onClick={onCancel}>
          Cancel
        </Clickable>
      </div>

      <p className="DeliveryZipForm__hint" id="delivery-zip-hint" aria-live="polite">
        {error && (
          <>
            <AlertIcon className="DeliveryZipForm__icon" width="14px" height="14px" aria-hidden />
            {error}
          </>
        )}

        {!error && current?.status === 'found' && (
          <>
            <CheckmarkIcon
              className="DeliveryZipForm__icon"
              width="14px"
              height="14px"
              aria-hidden
            />
            <span className="DeliveryZipForm__city">
              {current.city}, {current.state}
            </span>
            <span>· delivery dates updated</span>
          </>
        )}

        {!error && current?.status !== 'found' && HINT_COPY}
      </p>
    </form>
  );
};
